import React, { FC } from 'react';
import { HashLink } from 'react-router-hash-link';
import styled from 'styled-components';
import { FlexDynamic } from '@minecraft.atoms';
import _isEmpty from 'lodash/isEmpty';
import { SCREEN_NAMES } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import ProfileViewContext from '../ProfileViewContext';

const Link: FC<{ href: string }> = ({ children, href, ...restProps }) => (
  <HashLink to={href} {...restProps}>
    {children}
  </HashLink>
);

const LinkContainer = styled(Link)`
  padding: 0 2rem 0 0;
`;

const ProfileTopNav = () => {
  const { t } = useTranslation();

  const {
    showRepDetails,
    showSummary,
    isPreview,
    isPublic,
    fetcher: { profile },
  } = React.useContext(ProfileViewContext);

  const showDetailsLink = !isPreview && showRepDetails;

  const hasSizes =
    !_isEmpty(profile.profileTshirtSizes) ||
    !_isEmpty(profile.profileShoeSizes) ||
    !_isEmpty(profile.profilePants) ||
    profile.waist ||
    profile.inseam ||
    profile.gloveSize ||
    profile.hatSize ||
    profile.chestSize ||
    profile.sleeve ||
    profile.neck ||
    profile.coat ||
    profile.dressSize ||
    profile.bust ||
    profile.underbust ||
    profile.cupSize ||
    profile.hipSize;

  const showLink = (subSection: string): boolean => {
    if (isPreview) {
      return subSection === 'sizes' ? !!hasSizes : !_isEmpty(profile[subSection]);
    }

    return true;
  };

  return (
    <FlexDynamic
      hidden
      justifyContent="flex-start"
      direction="row"
      wrap="wrap"
      borderPlacement="bottom"
      mdProps={{ hidden: false }}
      mt="4"
      mb="8"
      w="full"
      className="cn_atom_pb-2"
      data-testid="profile-top-nav-wrapper"
    >
      {showSummary && (
        <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-summary-link`} key="summary" href="#div-summary">
          {t('common:profile.nav.summary')}
        </LinkContainer>
      )}
      {!showSummary && (
        <LinkContainer
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-basic-info-link`}
          key="basic-info"
          href="#div-basic-info"
        >
          {t('common:profile.nav.basicInfo')}
        </LinkContainer>
      )}
      <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-media-link`} key="media" href="#div-media">
        {t('common:profile.nav.media')}
      </LinkContainer>
      {showLink('credits') && (
        <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-credits-link`} key="credits" href="#div-credits">
          {t('common:profile.nav.credits')}
        </LinkContainer>
      )}
      {showLink('resume') && (
        <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-resume-link`} key="resume" href="#div-resume">
          {t('common:profile.nav.resume')}
        </LinkContainer>
      )}
      {showLink('skills') && (
        <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-skills-link`} href="#div-skills" key="skills">
          {t('common:profile.nav.skills')}
        </LinkContainer>
      )}
      {!isPreview && !isPublic && (
        <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-assets-link`} key="assets" href="#div-assets">
          {t('common:profile.nav.assets')}
        </LinkContainer>
      )}
      {showLink('sizes') && (
        <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-sizes-link`} href="#div-sizes" key="sizes">
          {t('common:profile.nav.sizes')}
        </LinkContainer>
      )}
      {showDetailsLink && (
        <LinkContainer data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-details-link`} key="details" href="#div-details">
          {t('common:profile.nav.details')}
        </LinkContainer>
      )}
      {!isPreview && [
        <LinkContainer
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-appearance-link`}
          key="appearance"
          href="#div-appearance"
        >
          {t('common:profile.nav.appearance')}
        </LinkContainer>,
        <LinkContainer
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-role-preferences-link`}
          key="preferences"
          href="#div-role-preferences"
        >
          {t('common:profile.nav.rolePreferences')}
        </LinkContainer>,
      ]}
    </FlexDynamic>
  );
};

export default ProfileTopNav;
